import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "@/components/Toast";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowLeft, Search, Plus, Trash2, User, UserPlus, FileText, Paperclip, X, Download, LogIn, LogOut, Loader2 } from "lucide-react";
import { formatDateTimeAR, formatTimeAR } from "@/lib/i18n";
import { roleLabel } from "@/lib/roles";
import { ExportButtons } from "@/components/ExportButtons"; // Fix BS
import { AttachmentPicker } from "@/components/AttachmentPicker";
import { fileSrc, uploadFile } from "@/lib/uploads";
import { DniLookup, NameDniSearch } from "@/components/DniLookup";

export default function AuthorizedPersons() {
  const { user } = useAuth();
  const isMobile = user?.role !== "admin";
  const backLink = user?.role === "admin" ? "/admin" : user?.role === "supervisor" ? "/supervisor" : "/vigilador";
  const canEdit = ["admin", "supervisor"].includes(user?.role);

  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [q, setQ] = useState("");
  const [list, setList] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", surname: "", dni: "", notes: "" });
  const [photo, setPhoto] = useState([]);
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [uploadingFor, setUploadingFor] = useState(null);
  // Fix BN (2026-11-03): ver Visits.jsx; solo se limpian los nombres completados solos.
  const [autoNames, setAutoNames] = useState(false);
  // Fix BN: quién está adentro (por DNI) para mostrar el botón correcto, y sede deducida del turno.
  const [insideByDni, setInsideByDni] = useState({});
  const [mySiteId, setMySiteId] = useState("");
  const [movingId, setMovingId] = useState(null);
  const dniKey = (d) => (d || "").replace(/\D/g, "");

  const loadInside = async (cid = clientId) => {
    if (!cid) return;
    try {
      const { data } = await api.get("/visits/active", { params: { client_id: cid } });
      const map = {};
      (data.clients || []).forEach((c) => c.people.forEach((p) => { map[dniKey(p.dni)] = p; }));
      setInsideByDni(map);
    } catch { setInsideByDni({}); }
  };
  useEffect(() => {
    loadInside(clientId);
    setMySiteId("");
    if (!clientId || user?.role === "admin") return;
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get(`/clients/${clientId}/materials/my-site`);
        if (!cancel && data?.site_id) setMySiteId(data.site_id);
      } catch { /* sin sede deducida */ }
    })();
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const move = async (p, direction) => {
    setMovingId(p.id);
    try {
      await api.post(`/authorized/${p.id}/movement`, { direction, site_id: mySiteId || null });
      toast.success(`${direction === "entrada" ? "Ingreso" : "Egreso"} registrado: ${p.name} ${p.surname}`);
      await loadInside();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setMovingId(null); }
  };

  useEffect(() => { (async () => {
    try { const r = await api.get("/clients"); setClients(r.data); if (r.data[0]) setClientId(r.data[0].id); }
    catch (e) { toast.error(formatApiError(e)); }
  })(); }, []);

  const load = async () => {
    if (!clientId) return;
    try {
      const r = await api.get(`/clients/${clientId}/authorized${q ? `?q=${encodeURIComponent(q)}` : ""}`);
      setList(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId, q]);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/authorized", {
        client_id: clientId,
        name: form.name, surname: form.surname, dni: form.dni, notes: form.notes,
        photo_url: photo[0]?.url || null,
        attachments: docs.map((d) => ({ url: d.url, filename: d.filename, content_type: d.content_type, size: d.size, kind: d.kind })),
      });
      toast.success("Persona autorizada");
      setForm({ name: "", surname: "", dni: "", notes: "" }); setPhoto([]); setDocs([]); setShowForm(false); setAutoNames(false);
      load();
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar este registro?")) return;
    try { await api.delete(`/authorized/${id}`); toast.success("Eliminado"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const addAttachment = async (personId, file) => {
    setUploadingFor(personId);
    try {
      const up = await uploadFile(file);
      await api.post(`/authorized/${personId}/attachments`, {
        url: up.url, filename: file.name, content_type: up.content_type, size: up.size, kind: up.kind,
      });
      toast.success("Planilla adjuntada");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploadingFor(null); }
  };

  const removeAttachment = async (personId, attId) => {
    if (!window.confirm("¿Quitar este adjunto?")) return;
    try { await api.delete(`/authorized/${personId}/attachments/${attId}`); toast.success("Adjunto eliminado"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div style={{ padding: isMobile ? "8px 16px" : "20px 32px", maxWidth: 1200, margin: "0 auto" }}>
      <Link to={backLink} style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--invict-text-muted)", fontSize: 12, marginBottom: 12, textDecoration: "none", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}><ArrowLeft size={14}/> Volver</Link>
      <h1 style={{ fontSize: 26, fontWeight: 900, margin: "4px 0 18px", color: "#0A0A0A", letterSpacing: "-0.02em" }}>Personal Autorizado</h1>

      <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
        <select value={clientId} onChange={(e) => setClientId(e.target.value)} style={{ flex: "1 1 200px", padding: "10px", border: "1px solid var(--invict-bone-3)", background: "#fff" }} data-testid="auth-client-select">
          {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <div style={{ flex: "2 1 250px", display: "flex", alignItems: "center", border: "1px solid var(--invict-bone-3)", background: "#fff", padding: "0 12px" }}>
          <Search size={14} style={{ color: "var(--invict-text-muted)" }} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre, apellido o DNI..." style={{ flex: 1, border: "none", padding: "10px", outline: "none" }} data-testid="auth-search" />
        </div>
        {canEdit && (
          <ExportButtons kind="authorized" rows={list} testIdPrefix="auth"
            filtersLabel={[`Objetivo: ${clients.find((c) => c.id === clientId)?.name || ""}`, q && `Búsqueda: "${q}"`].filter(Boolean).join(" · ")} />
        )}
        {canEdit && (
          <button onClick={() => setShowForm((v) => !v)} className="btn-primary" style={{ padding: "10px 16px" }} data-testid="auth-add-toggle">
            <Plus size={14} style={{ marginRight: 6 }} />{showForm ? "Cancelar" : "Nuevo"}
          </button>
        )}
      </div>

      {showForm && canEdit && (
        <form onSubmit={submit} className="card" style={{ padding: 16, marginBottom: 18 }} data-testid="auth-form">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
            <div className="field"><label>Nombre *</label><input required value={form.name} onChange={(e) => { setForm({ ...form, name: e.target.value }); setAutoNames(false); }} data-testid="auth-name"/></div>
            <div className="field"><label>Apellido *</label><input required value={form.surname} onChange={(e) => { setForm({ ...form, surname: e.target.value }); setAutoNames(false); }} data-testid="auth-surname"/></div>
            <div className="field"><label>DNI *</label><input required value={form.dni} onChange={(e) => {
              const dni = e.target.value;
              setForm((f) => (autoNames ? { ...f, dni, name: "", surname: "" } : { ...f, dni }));
              if (autoNames) setAutoNames(false);
            }} data-testid="auth-dni"/></div>
          </div>
          <DniLookup dni={form.dni} testIdPrefix="auth"
            onResolved={({ name, surname }) => { setForm((f) => ({ ...f, name, surname })); setAutoNames(true); }} />
          <NameDniSearch name={form.name} surname={form.surname} testIdPrefix="auth"
            onPick={(p) => { setForm((f) => ({ ...f, dni: p.dni, name: p.name, surname: p.surname })); setAutoNames(true); }} />
          <div className="field"><label>Notas</label><textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="ej. Personal de mantenimiento, horario de acceso..." /></div>
          <div className="field"><label>Foto (opcional)</label>
            <AttachmentPicker value={photo} onChange={setPhoto} accept="image" max={1} label="Subir foto"/>
          </div>
          <div className="field"><label>Planillas / documentos (opcional · PDF, Excel, Word)</label>
            <AttachmentPicker value={docs} onChange={setDocs} accept="document" max={6} label="Adjuntar planillas"/>
          </div>
          <button type="submit" disabled={busy} className="btn-primary" style={{ width: "100%", padding: 12 }} data-testid="auth-submit">{busy ? "Guardando..." : "Guardar"}</button>
        </form>
      )}

      <div className="card" style={{ padding: 0, overflow: "hidden" }} data-testid="auth-list">
        {list.length === 0 && <div style={{ padding: 30, textAlign: "center", color: "var(--invict-text-muted)" }}><UserPlus size={26} style={{ opacity: 0.4, margin: "0 auto 6px" }}/><div style={{ fontSize: 13 }}>Sin personas registradas para este cliente.</div></div>}
        {list.map((p) => {
          const isOpen = expanded === p.id;
          const attCount = (p.attachments || []).length;
          return (
            <div key={p.id} style={{ borderBottom: "1px solid var(--invict-bone-3)" }} data-testid={`auth-row-${p.id}`}>
              <div style={{ padding: "12px 16px", display: "flex", gap: 14, alignItems: "center" }}>
                <div style={{ width: 48, height: 48, borderRadius: "50%", overflow: "hidden", background: "var(--invict-bone-3)", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {p.photo_url ? <img src={fileSrc(p.photo_url)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }}/> : <User size={20} style={{ color: "var(--invict-text-muted)" }}/>}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 900, fontSize: 14, color: "#0A0A0A" }}>{p.surname}, {p.name}</div>
                  <div style={{ fontSize: 11, color: "var(--invict-text-muted)" }}>DNI {p.dni}</div>
                  {(p.dwelling_floor || p.dwelling_block) && (
                    <div style={{ fontSize: 11, color: "#BF3437", fontWeight: 700, marginTop: 2 }} data-testid={`auth-dwelling-${p.id}`}>
                      {p.dwelling_floor ? `Piso ${p.dwelling_floor}, Depto ${p.dwelling_unit}` : `Manzana ${p.dwelling_block}, Casa ${p.dwelling_house}`}
                    </div>
                  )}
                  {p.notes && <div style={{ fontSize: 11, color: "var(--invict-text-muted)", marginTop: 3, fontStyle: "italic" }}>{p.notes}</div>}
                  {p.created_by_name && (
                    <div style={{ fontSize: 11, color: "#0A0A0A", fontWeight: 700, marginTop: 3 }} data-testid={`auth-created-by-${p.id}`}>
                      Autorizado por {p.created_by_name}{p.created_by_role ? ` (${roleLabel(p.created_by_role)})` : ""}{p.created_at ? ` · ${formatDateTimeAR(p.created_at)}` : ""}
                    </div>
                  )}
                  {insideByDni[dniKey(p.dni)] && (
                    <div style={{ fontSize: 11, color: "#16a34a", fontWeight: 800, marginTop: 3 }} data-testid={`auth-inside-${p.id}`}>
                      Adentro desde las {formatTimeAR(insideByDni[dniKey(p.dni)].entrada_at)}
                    </div>
                  )}
                </div>
                {(() => {
                  const isInside = !!insideByDni[dniKey(p.dni)];
                  const dir = isInside ? "salida" : "entrada";
                  return (
                    <button type="button" onClick={() => move(p, dir)} disabled={movingId === p.id} data-testid={`auth-move-${p.id}`}
                      style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "8px 12px", fontSize: 11, fontWeight: 800, textTransform: "uppercase",
                        border: "none", borderRadius: 4, cursor: "pointer", color: "#fff", background: isInside ? "#BF3437" : "#16a34a", flexShrink: 0 }}>
                      {movingId === p.id ? <Loader2 size={13} className="spin" /> : isInside ? <LogOut size={13} /> : <LogIn size={13} />}
                      {isInside ? "Egreso" : "Ingreso"}
                    </button>
                  );
                })()}
                <button onClick={() => setExpanded(isOpen ? null : p.id)} style={{ display: "inline-flex", alignItems: "center", gap: 4, background: attCount > 0 ? "var(--invict-bone-2)" : "transparent", border: "1px solid var(--invict-bone-3)", color: "#0A0A0A", padding: "5px 10px", borderRadius: 2, fontSize: 11, fontWeight: 700, cursor: "pointer" }} data-testid={`auth-attachments-toggle-${p.id}`}>
                  <Paperclip size={12}/> {attCount} adj.
                </button>
                {canEdit && <button onClick={() => del(p.id)} style={{ background: "none", border: "none", color: "#BF3437", cursor: "pointer", padding: 6 }} data-testid={`auth-del-${p.id}`}><Trash2 size={16}/></button>}
              </div>

              {isOpen && (
                <div style={{ padding: "10px 16px 16px", background: "var(--invict-bone-2)" }} data-testid={`auth-attachments-${p.id}`}>
                  {attCount === 0 && (
                    <div style={{ fontSize: 11, color: "var(--invict-text-muted)", padding: "6px 0 10px" }}>
                      Sin planillas adjuntas todavía.
                    </div>
                  )}
                  {attCount > 0 && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 10 }}>
                      {p.attachments.map((a) => (
                        <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 8, background: "#fff", border: "1px solid var(--invict-bone-3)", padding: "8px 10px", borderRadius: 2 }} data-testid={`auth-att-${a.id}`}>
                          <FileText size={14} style={{ color: "#BF3437", flexShrink: 0 }}/>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: "#0A0A0A", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{a.filename || "Documento"}</div>
                            <div style={{ fontSize: 10, color: "var(--invict-text-muted)" }}>{a.content_type || "—"} · {a.size ? `${Math.round(a.size/1024)} KB` : ""} · {a.uploaded_by_name}</div>
                          </div>
                          <a href={fileSrc(a.url)} target="_blank" rel="noreferrer" style={{ color: "#0A0A0A", padding: 4 }} data-testid={`auth-att-open-${a.id}`}><Download size={14}/></a>
                          {canEdit && (
                            <button onClick={() => removeAttachment(p.id, a.id)} style={{ background: "none", border: "none", color: "#BF3437", cursor: "pointer", padding: 4 }} data-testid={`auth-att-del-${a.id}`}><X size={14}/></button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {canEdit && (
                    <label style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 12px", border: "2px dashed var(--invict-bone-3)", background: "#fff", color: "#0A0A0A", fontWeight: 700, fontSize: 11, borderRadius: 2, cursor: uploadingFor === p.id ? "wait" : "pointer", textTransform: "uppercase", letterSpacing: "0.05em" }} data-testid={`auth-att-upload-${p.id}`}>
                      <Paperclip size={12}/>
                      {uploadingFor === p.id ? "Subiendo..." : "Adjuntar planilla (PDF/Excel/Word)"}
                      <input type="file" accept="application/pdf,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/csv" style={{ display: "none" }}
                        onChange={(e) => { const f = e.target.files?.[0]; if (f) addAttachment(p.id, f); e.target.value = ""; }}
                        disabled={uploadingFor === p.id}/>
                    </label>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
